/**
 * Client-side dedupe for parallel availability fetches (map + sidebar on the same page).
 */

const CACHE_MS = 45_000;
const cache = new Map();
const inflight = new Map();

const cacheKey = (eventId, quantity, listingId) =>
  `${String(eventId).trim()}|${String(quantity)}|${String(listingId || '').trim()}`;

/**
 * @returns {Promise<{ ok: boolean, status: number, data: object|null }>}
 */
export const fetchTnEventAvailability = async (eventId, quantity, listingId) => {
  const id = String(eventId || '').trim();
  const qty = Number(quantity);
  const safeQty = Number.isFinite(qty) && qty >= 1 ? qty : 1;
  const listing = String(listingId || '').trim();
  if (!id) {
    return { ok: false, status: 400, data: { error: 'eventId is required' } };
  }

  const key = cacheKey(id, safeQty, listing);
  const hit = cache.get(key);
  if (hit && Date.now() < hit.expiresAt) {
    return hit.result;
  }
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const listingQuery = listing ? `&listingId=${encodeURIComponent(listing)}` : '';
  const url = `/api/tn-seatics/events/${encodeURIComponent(id)}/availability?quantity=${encodeURIComponent(
    safeQty
  )}${listingQuery}`;

  const promise = (async () => {
    const res = await fetch(url, { credentials: 'same-origin' });
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    let data = null;
    if (contentType.includes('application/json')) {
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }
    } else {
      const text = await res.text();
      data = text ? { error: text } : null;
    }
    const result = { ok: res.ok, status: res.status, data };
    if (res.ok) {
      cache.set(key, { result, expiresAt: Date.now() + CACHE_MS });
    }
    return result;
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(key);
  }
};

export const clearTnAvailabilityClientCache = () => {
  cache.clear();
  inflight.clear();
};
